import { Fragment, useEffect, useRef, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { fmtVND, fmtDate } from '../utils/format';
import { getScanStatus } from '../api/socson';
import client from '../api/client';
import {
  listBusinessReports,
  getBusinessReport,
  previewBusinessReport,
  submitBusinessGroup,
  updateBusinessGroupNote,
  getBusinessDraft,
  saveBusinessDraft,
} from '../api/businessReport';

const TOTAL_TAB = '__total__';

// Khối 2 phần note tay cho 1 nhóm: việc đã làm/kết quả + kế hoạch tuần tới.
// reportId có -> chế độ báo cáo đã chốt (tự lưu qua patch).
// controlled (value/onChange) -> chế độ "Tuần này" nhập trước, lưu khi bấm "Chốt tuần".
function GroupNotes({ reportId, group, canEdit, onSaved, value, onChange, onSaveDraft }) {
  const toast = useToast();
  const controlled = !reportId;
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [local, setLocal] = useState({
    doneThisWeek: group.doneThisWeek || '',
    doneResult: group.doneResult || '',
    nextWeekPlan: group.nextWeekPlan || '',
    nextWeekGoal: group.nextWeekGoal || '',
  });
  const f = controlled ? (value || { doneThisWeek: '', doneResult: '', nextWeekPlan: '', nextWeekGoal: '' }) : local;
  const [saving, setSaving] = useState(false);
  const orig = {
    doneThisWeek: group.doneThisWeek || '',
    doneResult: group.doneResult || '',
    nextWeekPlan: group.nextWeekPlan || '',
    nextWeekGoal: group.nextWeekGoal || '',
  };
  const dirty = !controlled && Object.keys(orig).some((k) => f[k] !== orig[k]);
  const set = (k) => (e) => {
    if (controlled) onChange(group.groupName, k, e.target.value);
    else setLocal((p) => ({ ...p, [k]: e.target.value }));
  };

  async function save() {
    setSaving(true);
    try {
      await updateBusinessGroupNote(reportId, { groupName: group.groupName, ...f });
      toast('Đã lưu báo cáo nhóm');
      onSaved && onSaved();
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi lưu báo cáo', 'danger');
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    setSavingDraft(true);
    try {
      await onSaveDraft(group.groupName, f);
      setSavedDraft(true);
      setTimeout(() => setSavedDraft(false), 2500);
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi lưu nháp', 'danger');
    } finally {
      setSavingDraft(false);
    }
  }

  const field = (label, k, ph) => (
    <div style={{ flex: '1 1 280px', minWidth: 240 }}>
      <label>{label}</label>
      {canEdit ? (
        <textarea rows={4} value={f[k]} onChange={set(k)} placeholder={ph} />
      ) : (
        <div className="small" style={{ whiteSpace: 'pre-wrap', minHeight: 20 }}>{f[k] || <span className="muted">— Chưa có —</span>}</div>
      )}
    </div>
  );

  return (
    <div className="card" style={{ marginTop: 12, background: 'var(--panel2)' }}>
      <div className="row between" style={{ alignItems: 'center', marginBottom: 8 }}>
        <b>🏷️ {group.groupName} — Báo cáo công việc tuần</b>
        {canEdit && dirty && (
          <button className="btn sm" disabled={saving} onClick={save}>{saving ? 'Đang lưu…' : '💾 Lưu'}</button>
        )}
        {controlled && canEdit && onSaveDraft && (
          <button className="btn sm" disabled={savingDraft} onClick={saveDraft}>
            {savingDraft ? 'Đang lưu…' : savedDraft ? '✓ Đã lưu nháp' : '✅ Lưu nháp'}
          </button>
        )}
      </div>
      <div className="small muted" style={{ fontWeight: 700, margin: '2px 0 4px' }}>1. Công việc trong tuần</div>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        {field('Công việc đã thực hiện', 'doneThisWeek', 'Liệt kê các việc đã triển khai…')}
        {field('Kết quả thực hiện', 'doneResult', 'Kết quả đạt được…')}
      </div>
      <div className="small muted" style={{ fontWeight: 700, margin: '12px 0 4px' }}>2. Tuần tới</div>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        {field('Kế hoạch tuần tới', 'nextWeekPlan', 'Kế hoạch dự kiến…')}
        {field('Mục tiêu', 'nextWeekGoal', 'Mục tiêu cần đạt…')}
      </div>
    </div>
  );
}

function ratioColor(r) {
  if (r >= 60) return 'var(--ok)';
  if (r >= 40) return 'var(--warn)';
  return 'var(--danger)';
}
function doneColor(p) {
  if (p >= 100) return 'var(--ok)';
  if (p >= 70) return 'var(--warn)';
  return 'var(--danger)';
}

function StatusChip({ submitted, by }) {
  return submitted ? (
    <span className="chip done" title={by ? `Đã gửi bởi ${by}` : 'Đã gửi'}>✓ Đã gửi</span>
  ) : (
    <span className="chip" style={{ background: 'var(--warn-bg, transparent)', color: 'var(--warn)' }}>● Chưa gửi</span>
  );
}

// 4 thẻ số tổng cho 1 phạm vi (toàn công ty hoặc 1 nhóm).
function MetricCards({ data }) {
  return (
    <div className="row" style={{ gap: 24, flexWrap: 'wrap', margin: '4px 0 14px' }}>
      <div>
        <div className="small muted">Tổng doanh thu</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent2)' }}>{fmtVND(data.totalRevenue)}</div>
      </div>
      <div>
        <div className="small muted">Khoán tháng</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtVND(data.totalTarget || 0)}</div>
      </div>
      <div>
        <div className="small muted">Tỷ lệ hoàn thành</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: doneColor(data.donePct || 0) }}>{data.donePct || 0}%</div>
      </div>
      <div>
        <div className="small muted">Tỷ lệ cọc chung</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: ratioColor(data.ratio) }}>{data.ratio}%</div>
      </div>
      <div>
        <div className="small muted">Số KH có DT</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{data.totalCust || 0}</div>
      </div>
      <div>
        <div className="small muted">KH hỗ trợ</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)' }}>{data.totalSupportCust || 0}</div>
      </div>
      {data.period && (
        <div>
          <div className="small muted">Kỳ quét</div>
          <div style={{ fontWeight: 700 }}>{fmtDate(data.period.from)} → {fmtDate(data.period.to)}</div>
        </div>
      )}
    </div>
  );
}

// Hàng tiêu đề cột dùng chung: Mã NV | Nhân viên | Doanh thu | Khoán | HT | Cọc.
function MemberHead() {
  return (
    <thead>
      <tr>
        <th>Mã NV</th>
        <th>Nhân viên</th>
        <th style={{ textAlign: 'right' }}>Doanh thu</th>
        <th style={{ textAlign: 'right' }}>HT</th>
        <th style={{ textAlign: 'right' }}>Cọc</th>
        <th style={{ textAlign: 'right' }} title="Số KH phát sinh doanh thu trong tháng">KH</th>
        <th style={{ textAlign: 'right' }} title="Số KH phát sinh doanh thu hỗ trợ">KH HT</th>
      </tr>
    </thead>
  );
}
function MemberRow({ m, indent }) {
  return (
    <tr>
      <td className="small muted">{m.employeeCode || '—'}</td>
      <td style={indent ? { paddingLeft: 18 } : undefined}>
        {m.name}
        {m.isDeputy ? (
          <span className="tag" style={{ marginLeft: 6, color: 'var(--warn)' }}>PP</span>
        ) : m.role === 'TP' ? (
          <span className="tag" style={{ marginLeft: 6, background: 'var(--accent-bg, transparent)', color: 'var(--accent)' }}>TP</span>
        ) : null}
      </td>
      <td style={{ textAlign: 'right' }}>
        {/* Doanh thu chính TRÊN, phần hỗ trợ (xanh) XUỐNG DÒNG DƯỚI — tiết kiệm chiều ngang. */}
        <div>{fmtVND(m.revenue)}</div>
        {(Number(m.support) || 0) > 0 && <div style={{ color: '#16a34a', fontWeight: 600, fontSize: 12 }}>({fmtVND(m.support)})</div>}
      </td>
      <td style={{ textAlign: 'right', color: doneColor(m.donePct || 0), fontWeight: 600 }}>{m.target ? `${m.donePct || 0}%` : '—'}</td>
      <td style={{ textAlign: 'right', color: ratioColor(m.ratio), fontWeight: 600 }}>{m.ratio}%</td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>{m.custCount || 0}</td>
      <td style={{ textAlign: 'right', color: 'var(--accent)', fontWeight: 600 }}>{m.supportCustCount || 0}</td>
    </tr>
  );
}

// Bảng nhân viên của 1 NHÓM (tab nhóm).
function MemberTable({ members }) {
  return (
    <div className="tbl-wrap framed">
      <table style={{ fontSize: 13 }}>
        <MemberHead />
        <tbody>
          {(members || []).map((m, i) => <MemberRow key={i} m={m} />)}
          {!(members || []).length && <tr><td colSpan={7} className="empty">Chưa có nhân viên.</td></tr>}
        </tbody>
      </table>
      <div className="small muted" style={{ padding: '6px 8px' }}>
        Số <span style={{ color: '#16a34a', fontWeight: 600 }}>(xanh)</span> ở cột doanh thu = phần doanh thu hỗ trợ (nhận từ nhóm khác / đi hỗ trợ nhóm khác), đã gồm trong doanh thu cá nhân. Tổng nhóm chỉ tính mỗi đồng 1 lần.
      </div>
    </div>
  );
}

// Bảng gộp tất cả nhóm (tab Tổng): hàng tiêu đề nhóm + nhân viên.
function GroupedTable({ groups }) {
  return (
    <div className="tbl-wrap framed">
      <table style={{ fontSize: 13 }}>
        <MemberHead />
        <tbody>
          {(groups || []).map((g) => (
            <Fragment key={g.groupName}>
              <tr style={{ background: 'var(--panel2)' }}>
                <td style={{ fontWeight: 800 }}></td>
                <td style={{ fontWeight: 800 }}>🏷️ {g.groupName}</td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--accent2)' }}>{fmtVND(g.totalRevenue)}</td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: doneColor(g.donePct || 0) }}>{g.donePct || 0}%</td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: ratioColor(g.ratio) }}>{g.ratio}%</td>
                <td style={{ textAlign: 'right', fontWeight: 800 }}>{g.totalCust || 0}</td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--accent)' }}>{g.totalSupportCust || 0}</td>
              </tr>
              {(g.members || []).map((m, i) => <MemberRow key={g.groupName + i} m={m} indent />)}
            </Fragment>
          ))}
          {!(groups || []).length && <tr><td colSpan={7} className="empty">Chưa có nhóm/nhân viên nào.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function BusinessReportPage() {
  const [scanning, setScanning] = useState(false);
  const toast = useToast();
  const { me } = useAuth();
  const admin = me.role === 'GD' || me.role === 'CV';
  const canEditGroup = (groupName) => admin || (me.role === 'TP' && me.group?.name === groupName);
  // Nút "Quét doanh thu tuần": chỉ GĐ/CV + Trưởng phòng kinh doanh (TP có nhóm) mới thấy;
  // và chỉ bật SAU 16:30 thứ 5 (làm dự phòng khi quét tự động không chạy).
  const canScanBiz = admin || (me.role === 'TP' && !!me.group);
  const afterThu1630 = (() => {
    const d = new Date(); const w = d.getDay(); // 0=CN..6=T7
    const late = d.getHours() > 16 || (d.getHours() === 16 && d.getMinutes() >= 30);
    return (w === 4 && late) || w === 5 || w === 6 || w === 0; // T5(sau 16:30) → CN
  })();
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null); // báo cáo tuần đang xem (đã chốt)
  const [activeId, setActiveId] = useState(null);
  const [preview, setPreview] = useState(null); // bảng hiện tại (chưa chốt)
  const [mode, setMode] = useState('preview'); // 'report' | 'preview'
  const [tab, setTab] = useState(TOTAL_TAB); // tab đang xem: '__total__' | groupName
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(''); // groupName đang gửi
  const [pnotes, setPnotes] = useState({}); // note nhập trước khi chốt, theo nhóm
  const scroller = useRef(null);

  function seedPreview(p) {
    setPreview(p);
    setPnotes((prev) => {
      const next = { ...prev };
      (p?.groups || []).forEach((g) => {
        if (!next[g.groupName]) {
          next[g.groupName] = {
            doneThisWeek: g.doneThisWeek || '', doneResult: g.doneResult || '',
            nextWeekPlan: g.nextWeekPlan || '', nextWeekGoal: g.nextWeekGoal || '',
          };
        }
      });
      return next;
    });
    return p;
  }
  const onPnoteChange = (groupName, k, v) =>
    setPnotes((prev) => ({ ...prev, [groupName]: { ...prev[groupName], [k]: v } }));

  function loadDraft() {
    return getBusinessDraft()
      .then((d) => {
        const notes = d?.notes || {};
        setPnotes((prev) => {
          const next = { ...prev };
          Object.keys(notes).forEach((gn) => { next[gn] = { ...next[gn], ...notes[gn] }; });
          return next;
        });
      })
      .catch(() => {});
  }
  const onSaveDraft = (groupName, fields) => saveBusinessDraft({ groupName, ...fields });

  function loadReports() {
    return listBusinessReports().then((list) => {
      setReports(list);
      return list;
    });
  }
  function reload() {
    setLoading(true);
    Promise.all([
      loadReports().then((list) => { if (list.length) openReport(list[list.length - 1]); }),
      previewBusinessReport().then(seedPreview).then(loadDraft).catch(() => {}),
    ]).finally(() => setLoading(false));
  }
  useEffect(reload, []);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [reports]);

  async function openReport(r) {
    setActiveId(r._id);
    const full = await getBusinessReport(r._id);
    setActive(full);
  }

  // Quét doanh thu + tỷ lệ cọc tháng NGAY TRÊN SERVER (dùng token SocSon đã lưu) rồi chốt
  // báo cáo tuần — không cần tab/extension. Job chạy nền; poll tới khi xong.
  async function scan() {
    setScanning(true);
    try {
      await client.post('/socson/scan/business-weekly');
      toast('Đã bắt đầu quét doanh thu tuần ở nền…');
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const m = await getScanStatus().catch(() => null);
        if (m?.businessWeekly && m.businessWeekly.status !== 'running') {
          if (m.businessWeekly.status === 'error') { toast(`Lỗi quét: ${m.businessWeekly.lastError || '?'}`, 'danger'); break; }
          const list = await loadReports();
          previewBusinessReport().then(seedPreview).catch(() => {});
          if (list.length) openReport(list[list.length - 1]);
          break;
        }
      }
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi khi bắt đầu quét', 'danger');
    } finally {
      setScanning(false);
    }
  }

  // TP (hoặc GĐ/CV) chốt & gửi báo cáo của MỘT nhóm.
  async function submitGroup(groupName) {
    if (!await confirm(`Chốt & gửi báo cáo nhóm ${groupName}? Số liệu doanh thu nhóm sẽ được khoá cho tuần này.`)) return;
    setSubmitting(groupName);
    try {
      await submitBusinessGroup(groupName, pnotes[groupName] || {});
      toast(`Đã gửi báo cáo nhóm ${groupName}`);
      await Promise.all([previewBusinessReport().then(seedPreview).catch(() => {}), loadReports()]);
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi gửi báo cáo nhóm', 'danger');
    } finally {
      setSubmitting('');
    }
  }

  const source = mode === 'report' ? active : preview;
  const groups = source?.groups || [];
  const tabs = source ? [TOTAL_TAB, ...groups.map((g) => g.groupName)] : [];
  // Nếu tab hiện tại không còn (đổi mode/dữ liệu) -> về tab Tổng.
  useEffect(() => {
    if (source && !tabs.includes(tab)) setTab(TOTAL_TAB);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeGroup = tab !== TOTAL_TAB ? groups.find((g) => g.groupName === tab) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Báo cáo kinh doanh</h2>
          <div className="sub">
            Mỗi trưởng phòng kinh doanh chốt &amp; gửi báo cáo doanh thu nhóm mình. Quét lũy kế từ 0h ngày 1 đầu tháng → thời điểm quét; tự động thứ 5 hằng tuần ~16:30.
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {canScanBiz && (
            <button className="btn ghost" disabled={scanning || !afterThu1630} onClick={scan}
              title={!afterThu1630 ? 'Chỉ quét thủ công sau 16:30 thứ 5 (dự phòng khi quét tự động lỗi)' : ''}>
              {scanning ? 'Đang quét…' : '🔄 Quét doanh thu tuần'}
            </button>
          )}
        </div>
      </div>
      <div className="card">
        <div className="seg" style={{ marginBottom: 12 }}>
          <button className={mode === 'preview' ? 'active' : ''} onClick={() => setMode('preview')}>Tuần này (trực tiếp)</button>
          <button className={mode === 'report' ? 'active' : ''} onClick={() => setMode('report')}>Báo cáo đã chốt</button>
        </div>

        {/* Dòng thời gian các tuần đã chốt (chế độ "đã chốt") */}
        {mode === 'report' && reports.length > 0 && (
          <div className="tl-wrap" style={{ marginBottom: 14 }}>
            <button className="tl-arrow" onClick={() => scroller.current?.scrollBy({ left: -400, behavior: 'smooth' })}>‹</button>
            <div className="tl-scroll" ref={scroller}>
              {reports.map((r) => (
                <div
                  key={r._id}
                  className={'cslog-cell' + (activeId === r._id ? ' thu' : '')}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openReport(r)}
                >
                  <div className="cslog-head">{r.label}</div>
                  {r.savedBy && <div className="small muted" style={{ textAlign: 'center', marginBottom: 4 }}>{r.savedBy}</div>}
                  <div className="cslog-row"><span>💰 DT</span><b>{fmtVND(r.totalRevenue)}</b></div>
                  <div className="cslog-row"><span>💳 Cọc</span><b>{r.ratio}%</b></div>
                </div>
              ))}
            </div>
            <button className="tl-arrow" onClick={() => scroller.current?.scrollBy({ left: 400, behavior: 'smooth' })}>›</button>
          </div>
        )}

        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : !source || (mode === 'report' && !active) ? (
          <div className="empty">{mode === 'report' ? 'Chọn một tuần ở trên.' : 'Chưa có dữ liệu.'}</div>
        ) : (
          <>
            {/* Thanh tab: Tổng + từng nhóm, kèm trạng thái đã gửi */}
            <div className="seg" style={{ marginBottom: 14, flexWrap: 'wrap', maxWidth: '100%' }}>
              {tabs.map((t) => {
                const g = t === TOTAL_TAB ? null : groups.find((x) => x.groupName === t);
                const label = t === TOTAL_TAB
                  ? `Tổng (${source.submittedCount ?? groups.filter((x) => x.submitted).length}/${source.totalGroups ?? groups.length})`
                  : t;
                return (
                  <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
                    {label}
                    {g && <span style={{ marginLeft: 6 }}>{g.submitted ? '✓' : '●'}</span>}
                  </button>
                );
              })}
            </div>

            {tab === TOTAL_TAB ? (
              <>
                <div className="small muted" style={{ marginBottom: 6 }}>
                  {mode === 'report'
                    ? `${active.label}${active.savedBy ? ` · chốt bởi ${active.savedBy}` : ''} · ${fmtDate(active.createdAt)}`
                    : `${preview?.label} — số liệu trực tiếp · ${source.submittedCount ?? 0}/${source.totalGroups ?? groups.length} nhóm đã gửi`}
                </div>
                <MetricCards data={source} />
                <GroupedTable groups={groups} />
              </>
            ) : activeGroup ? (
              <>
                <div className="row between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <div>
                    <b style={{ fontSize: 15 }}>🏷️ Nhóm {activeGroup.groupName}</b>{' '}
                    <StatusChip submitted={activeGroup.submitted} by={activeGroup.submittedBy} />
                    {activeGroup.submitted && activeGroup.submittedBy && (
                      <div className="small muted">Đã gửi bởi {activeGroup.submittedBy}</div>
                    )}
                  </div>
                  {mode === 'preview' && !activeGroup.submitted && canEditGroup(activeGroup.groupName) && (
                    <button className="btn" disabled={submitting === activeGroup.groupName} onClick={() => submitGroup(activeGroup.groupName)}>
                      {submitting === activeGroup.groupName ? 'Đang gửi…' : '📤 Chốt và gửi báo cáo nhóm'}
                    </button>
                  )}
                </div>
                <MetricCards data={activeGroup} />
                <MemberTable members={activeGroup.members} />
                {mode === 'report' ? (
                  <GroupNotes
                    key={`${active._id}-${activeGroup.groupName}`}
                    reportId={active._id}
                    group={activeGroup}
                    canEdit={admin}
                    onSaved={() => openReport({ _id: active._id })}
                  />
                ) : (
                  <GroupNotes
                    key={activeGroup.groupName}
                    group={activeGroup}
                    canEdit={!activeGroup.submitted && canEditGroup(activeGroup.groupName)}
                    value={activeGroup.submitted
                      ? {
                          doneThisWeek: activeGroup.doneThisWeek || '', doneResult: activeGroup.doneResult || '',
                          nextWeekPlan: activeGroup.nextWeekPlan || '', nextWeekGoal: activeGroup.nextWeekGoal || '',
                        }
                      : pnotes[activeGroup.groupName]}
                    onChange={onPnoteChange}
                    onSaveDraft={onSaveDraft}
                  />
                )}
              </>
            ) : (
              <div className="empty">Không tìm thấy nhóm.</div>
            )}
          </>
        )}
      </div>
    </>
  );
}
